import { useCallback, useEffect, useMemo, useRef } from 'react';
import { css } from '@emotion/react';
import { ScheduleKnowledge } from '@weave/schema-gen-ts/dist/schemas/ai-settings/v1/ai_settings.pb';
import dayjs from 'dayjs';
import isEqual from 'lodash-es/isEqual';
import { useGetAirSettingsQuery, useUpdateSchedulingAirSettingsMutation } from '@frontend/api-ai-settings';
import { Trans, useTranslation } from '@frontend/i18n';
import { useAppScopeStore } from '@frontend/scope';
import { FloatingSaveBar } from '@frontend/settings-ui';
import { sentry } from '@frontend/tracking';
import { useUnsavedChanges } from '@frontend/unsaved-changes-modal';
import { useAlert, useModalControl } from '@frontend/design-system';
import { AIRSettingsDefaultSourcesProvider } from '../../../../contexts/air-settings-default-sources.context';
import {
  useAIRScheduleKnowledgeStore,
  AIRScheduleKnowledgeProvider,
  selectStoreIsDirty,
} from '../../../../stores/air-settings.store';
import { AiReceptionistTrackingIds } from '../../../../tracking-ids';
import { LastUpdatedInfo } from '../../common/last-updated-info';
import { SettingsUnsavedChangesModal } from '../../common/settings-unsaved-changes-modal';
import { MultiLocationSchedulingInstruction } from './multi-location-scheduling-instruction';
import { SingleLocationSchedulingInstruction } from './single-location-scheduling-instruction';
import { SCHEDULING_MODES, SchedulingMode } from './types';

const DEFAULT_SCHEDULE_KNOWLEDGE: ScheduleKnowledge = {
  schedulingType: SCHEDULING_MODES.DO_NOT_HELP,
  cancellationPolicy: '',
};

const createInitialSettings = (locationIds: string[]): Record<string, ScheduleKnowledge> => {
  return locationIds.reduce((acc, id) => {
    acc[id] = { ...DEFAULT_SCHEDULE_KNOWLEDGE };
    return acc;
  }, {} as Record<string, ScheduleKnowledge>);
};

const SchedulingInstructionPageContent = () => {
  const { t } = useTranslation('ai-receptionist-settings');
  const { selectedLocationIds } = useAppScopeStore();
  const alerts = useAlert();

  const {
    initialSettings,
    draftSettings,
    setInitialSettings,
    setDraftSettings,
    updateDraft,
    saveDraft,
    clearDraft,
    resetStore,
  } = useAIRScheduleKnowledgeStore([
    'initialSettings',
    'draftSettings',
    'setInitialSettings',
    'setDraftSettings',
    'updateDraft',
    'saveDraft',
    'clearDraft',
    'resetStore',
  ]);

  const shouldProceedAfterSaveRef = useRef(false);

  const { data: airSettingsResponse, isLoading: isScheduleSettingsLoading } = useGetAirSettingsQuery({
    request: {
      locationIds: selectedLocationIds,
    },
    options: {
      enabled: selectedLocationIds.length > 0,
    },
  });

  const { mutate: updateSchedulingAirSettings, isLoading: isUpdatingSchedulingAirSettings } =
    useUpdateSchedulingAirSettingsMutation({
      options: {
        onSuccess: () => {
          alerts.success(t('Scheduling instruction settings saved successfully.'));
          saveDraft();
          if (shouldProceedAfterSaveRef.current) {
            shouldProceedAfterSaveRef.current = false;
            proceedWithUnsavedChanges();
          }
        },
        onError: (error) => {
          shouldProceedAfterSaveRef.current = false;
          alerts.error(t('Failed to save scheduling instruction settings. Please try again.'));
          sentry.error({
            team: 'frontend',
            topic: 'ai-receptionist',
            error,
          });
        },
      },
    });

  useEffect(() => {
    const scheduleSettings = airSettingsResponse?.locationAirSettings?.reduce((acc, curr) => {
      if (curr.locationId) {
        acc[curr.locationId] = {
          ...DEFAULT_SCHEDULE_KNOWLEDGE,
          ...(curr.airSettings?.scheduleKnowledge || {}),
        };
      }
      return acc;
    }, {} as Record<string, ScheduleKnowledge>);

    if (scheduleSettings && Object.keys(scheduleSettings).length > 0) {
      setInitialSettings(scheduleSettings);
      setDraftSettings(scheduleSettings);
    } else {
      const defaultSettings = createInitialSettings(selectedLocationIds);
      setInitialSettings(defaultSettings);
      setDraftSettings(defaultSettings);
    }
  }, [airSettingsResponse, selectedLocationIds, setInitialSettings, setDraftSettings]);

  const isMultiLocation = selectedLocationIds.length > 1;

  const handleSingleChange = (locationId: string, newMode: SchedulingMode) => {
    updateDraft(locationId, { schedulingType: newMode });
  };

  const isDirty = useAIRScheduleKnowledgeStore(selectStoreIsDirty);

  const { modalProps: unsavedChangesModalProps, openModal: openUnsavedChangesModal } = useModalControl();
  const { proceed: proceedWithUnsavedChanges, cancel: cancelUnsavedChanges } = useUnsavedChanges(
    isDirty,
    unsavedChangesModalProps.show,
    openUnsavedChangesModal
  );

  const handleSave = useCallback(() => {
    if (isUpdatingSchedulingAirSettings) return;

    const updatedLocationDataList = Object.entries(draftSettings)
      .filter(([locationId, settings]) => {
        const initialSettingsForLocation = initialSettings[locationId] || {};
        return !isEqual(settings, initialSettingsForLocation);
      })
      .map(([locationId, settings]) => ({
        ...settings,
        locationId,
      }));

    updateSchedulingAirSettings({
      scheduleKnowledge: updatedLocationDataList,
    });
  }, [isUpdatingSchedulingAirSettings, draftSettings, updateSchedulingAirSettings, initialSettings]);

  const handleProceedWithoutSaving = () => {
    clearDraft();
    unsavedChangesModalProps.onClose();
    proceedWithUnsavedChanges();
  };

  const lastUpdatedAt = useMemo(() => {
    const lastUpdatedAtList = airSettingsResponse?.locationAirSettings
      ?.map((settings) =>
        settings?.airSettings?.scheduleKnowledge?.lastUpdatedAt
          ? dayjs(settings.airSettings.scheduleKnowledge.lastUpdatedAt)
          : undefined
      )
      .filter((ts): ts is dayjs.Dayjs => !!ts)
      .sort((a, b) => b.valueOf() - a.valueOf());

    return lastUpdatedAtList?.[0]?.toISOString();
  }, [airSettingsResponse?.locationAirSettings]);

  useEffect(() => {
    return () => {
      resetStore();
    };
  }, []);

  const isLoading = isScheduleSettingsLoading || isUpdatingSchedulingAirSettings;

  return (
    <div css={styles.container}>
      <div css={{ flex: 1, paddingBottom: '80px' }}>
        {isMultiLocation ? (
          <MultiLocationSchedulingInstruction
            locationIds={selectedLocationIds}
            settings={draftSettings}
            initialSettings={initialSettings}
            onChange={handleSingleChange}
            isLoading={isLoading}
          />
        ) : (
          <SingleLocationSchedulingInstruction
            locationId={selectedLocationIds[0]}
            mode={
              (draftSettings[selectedLocationIds[0]]?.schedulingType as SchedulingMode) || SCHEDULING_MODES.DO_NOT_HELP
            }
            onChange={handleSingleChange}
            isLoading={isLoading}
          />
        )}
      </div>

      <FloatingSaveBar
        isDirty={isDirty}
        extraContent={lastUpdatedAt ? <LastUpdatedInfo lastUpdatedAt={lastUpdatedAt} /> : null}
        primaryAction={{
          label: t('Save'),
          onClick: handleSave,
          disabled: isScheduleSettingsLoading || isUpdatingSchedulingAirSettings,
          trackingId: AiReceptionistTrackingIds.schedulingInstructionSaveButton,
          hoverLabel: t('Click to update AI instructions. Changes should apply soon after being updated.'),
        }}
      />

      <SettingsUnsavedChangesModal
        show={unsavedChangesModalProps.show}
        onClose={() => {
          unsavedChangesModalProps.onClose();
          cancelUnsavedChanges();
        }}
        onProceedWithoutSaving={handleProceedWithoutSaving}
        onSave={() => {
          shouldProceedAfterSaveRef.current = true;
          handleSave();
          unsavedChangesModalProps.onClose();
        }}
        description={
          <Trans t={t}>
            Before you leave, <strong>would you like to save and push changes</strong> to your AI Receptionist's
            scheduling instructions?
          </Trans>
        }
      />
    </div>
  );
};

export const SchedulingInstructionPage = () => {
  const { selectedLocationIds } = useAppScopeStore();

  return (
    <AIRSettingsDefaultSourcesProvider locationIds={selectedLocationIds}>
      <AIRScheduleKnowledgeProvider>
        <SchedulingInstructionPageContent />
      </AIRScheduleKnowledgeProvider>
    </AIRSettingsDefaultSourcesProvider>
  );
};

const styles = {
  container: css`
    display: flex;
    flex-direction: column;
    position: relative;
    height: 100%;
  `,
};
